import axios from "axios";
import qs from "qs";
import store from "@/store";
import router from "@/router";
import { i18n } from "@/i18n";

const api = axios.create();

let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(token) {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback) {
  refreshSubscribers.push(callback);
}

function getRefreshTokenKeycloak() {
  return store.state.general.refreshTokenKeycloak;
}

function getTokenKeycloak() {
  return store.state.general.tokenKeycloak;
}

function forceLogout(reasonKey = "login.messages.disconnected") {
  store.commit("clearToken");
  store.commit("clearRefreshToken");

  if (router.currentRoute.name !== "logout") {
    router
      .push({
        name: "logout",
        params: {
          message: i18n.t(reasonKey),
          messageClass: "error"
        }
      })
      .catch(() => {});
  }
}

api.interceptors.request.use(config => {
  const token = getTokenKeycloak();
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise(resolve => {
          addRefreshSubscriber(token => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const refreshResponse = await axios.post(
          `${process.env.VUE_APP_KEYCLOAK_URL}realms/jyga/protocol/openid-connect/token`,
          qs.stringify({
            client_id: process.env.VUE_APP_KEYCLOAK_CLIENT_ID,
            client_secret: process.env.VUE_APP_KEYCLOAK_CLIENT_SECRET,
            grant_type: "refresh_token",
            refresh_token: getRefreshTokenKeycloak()
          }),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded"
            }
          }
        );

        const newToken = refreshResponse.data.access_token;
        const newRefreshToken = refreshResponse.data.refresh_token;

        store.commit("general/setTokenKeycloak", newToken);
        store.commit("general/setRefreshToken", newRefreshToken);

        onRefreshed(newToken);
        isRefreshing = false;

        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.error("Refresh token failed", refreshError);
        isRefreshing = false;
        forceLogout("login.messages.expired");
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
